import { Suspense, lazy, useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import Welcome from './pages/Welcome';
import EngineWorkspace from './pages/EngineWorkspace';
import WeeklyReview from './pages/WeeklyReview';
import CaseStudy from './pages/CaseStudy';
import Settings from './pages/Settings';
import BuildRoom from './pages/BuildRoom';
import AfterCheckout from './pages/AfterCheckout';
import { useAppAuth } from '@/lib/appAuth';

// LabHome retired 2026-05-11: Tom's call to collapse home into Lab Core.
// The route `lab-home` redirects to `core` via RETIRED_ROUTE_REDIRECTS;
// LabHome.tsx file remains for git-history reference and will be deleted
// in a follow-up cleanup PR.

// Code-split heavy chat-led pages so non-chat routes (Loop Install,
// Operator Dashboard, Academy) don't pay the streamdown + mermaid + cjk +
// math bundle cost.
// streamdown alone adds ~1.5MB to the main chunk.
const LabDesk        = lazy(() => import('./pages/LabDesk'));
const Community      = lazy(() => import('./pages/Community'));
const LoopInstallWorkspace = lazy(() => import('./pages/LoopInstallWorkspace'));
const OperatorDashboard = lazy(() => import('./pages/OperatorDashboard'));
// Legacy Command Centre remains as a compatibility file, but the canonical
// v0 operator surface is Operator Dashboard. Old deep links normalize there.
const CommandCentre  = lazy(() => import('./pages/CommandCentre'));
// Labs — portal hosting interactive Foundry tools (Lifestyle Target calculator, …).
const Labs           = lazy(() => import('./pages/Labs'));
// One product a member owns. Split because the classroom inside it carries the
// lesson editor, and the editor carries TipTap — a member landing anywhere else
// should not pay for it on first paint.
const LearningPortal = lazy(() => import('../../learning-journey/LearningPortalApp'));

/**
 * OSV2App -- Foundry Race Bib OS surface (parallel to legacy `src/surfaces/os/`).
 *
 * Mounted at `?surface=os-v2` for A/B against the legacy OS until validated.
 * Direction: docs/design/2026-04-26-design-direction.md
 * Plan: docs/plans/2026-04-26-foundry-design-system-redesign.md
 *
 * Light theme is the canonical default. OS-only dark-mode toggle is exposed
 * in the nav-rail footer (per-Builder preference, persisted to localStorage).
 */
export type OSV2Route =
  | 'welcome'
  | 'loop-install'
  | 'operator-dashboard'
  | 'command-centre'
  | 'core'
  // One product the buyer owns: `?osRoute=product&sku=<id>`. Unlike
  // `after-checkout` this IS a shell destination — rail, breadcrumb, top bar —
  // so it belongs in the union.
  | 'learning-work'
  | 'learning-resources'
  | 'journey'
  | 'product'
  | 'weekly-review'
  | 'build-room'
  | 'acquisition-engine'
  | 'experience-engine'
  | 'academy'
  | 'labs'
  | 'case-study'
  | 'lab'
  | 'community'
  | 'ask-tom'
  | 'settings';

const OSV2_ROUTES = new Set<OSV2Route>([
  'welcome',
  'loop-install',
  'operator-dashboard',
  'command-centre',
  'core',
  'product',
  'journey',
  'learning-work',
  'learning-resources',
  'weekly-review',
  'build-room',
  'acquisition-engine',
  'experience-engine',
  'academy',
  'labs',
  'case-study',
  'lab',
  'community',
  'ask-tom',
  'settings',
]);

// Legacy deep-link normalization map. Old bookmarks + email links land on
// the closest current surface.
//   - `command-centre` + `library` retired 2026-05-08 -- merged into the
//     sparse `lab-home` (Sprint C.2). The rail surfaces every product so
//     the prior three-product-home composition was redundant.
//   - `core` was retired 2026-05-08 then revived 2026-05-09 with new content
//     (the AI Brand Lab Core membership home -- module grid mirroring Build
//     Room). Old `?osRoute=core` bookmarks now resolve to the new surface,
//     not a redirect.
//   - `business-xray` + `strategy-discovery` retired 2026-05-07 (the diagnostic
//     now runs as gstack-style chained skills in the Builder's operating repo).
//   - `toolkit` + `artifacts` retired in Sprint F (audit ranked them as
//     dead-end surfaces; Toolkit had no working CTAs and Artifacts duplicated
//     the prior ProductLibrary's reports list).
// Retired-route redirects. Notes:
//   - `command-centre` is NOT retired (revived 2026-05-11 with a different
//     job: accountability + context, not product display).
//   - `lab-home` was the prior sparse home (Sprint C.2). Tom's
//     2026-05-11 call: collapse home into Lab Core so Builders land in
//     something useful (modules + premium product cards) instead of an
//     empty greeting. Old `lab-home` bookmarks redirect to `core`.
const RETIRED_ROUTE_REDIRECTS: Record<string, OSV2Route> = {
  'library': 'core',
  'lab-home': 'core',
  'business-xray': 'loop-install',
  'strategy-discovery': 'loop-install',
  'toolkit': 'loop-install',
  'artifacts': 'core',
  //   - `case-study` parked 2026-08-25 (SKU-shelf branch): the page carries
  //     figures and video metadata the file's own comments say are not yet
  //     real ("2.4x", "78% completion", timestamps for unrecorded videos) —
  //     an honesty-floor violation on a member-reachable page. Redirect to
  //     `core` until Tom supplies verified content; the component stays for
  //     that day.
  'case-study': 'core',
};

function normalizeRoute(route: OSV2Route): OSV2Route {
  // 2026-05-27 reset: Company Brain + First Loop Installation Sprint is the
  // parent product. Legacy acquisition/workshop routes land on the guided
  // Loop Installation Workspace; Command Centre lands on Operator Dashboard.
  if (route === 'acquisition-engine' || route === 'build-room' || route === 'weekly-review') {
    return 'loop-install';
  }
  if (route === 'command-centre') return 'operator-dashboard';
  // Community is now its own first-party surface -- no longer redirected to
  // The Lab support desk.
  return route;
}

/**
 * URL-param routing -- `?osRoute=lab-home` deep-links to a page so the
 * Builder can share or refresh on a route without losing it. Falls back to
 * the Lab home for unknown / missing values.
 *
 * Retired routes (e.g. `command-centre`, `core`, `library` -- merged into
 * `lab-home` in Sprint C.2; `business-xray`, `strategy-discovery` -- the
 * diagnostic now runs as gstack-style chained skills in the Builder's
 * operating workspace) redirect via RETIRED_ROUTE_REDIRECTS so old bookmarks
 * and email links don't 404.
 */
function getInitialRoute(): OSV2Route {
  const requested = new URLSearchParams(window.location.search).get('osRoute');
  if (requested) {
    if (RETIRED_ROUTE_REDIRECTS[requested]) {
      return RETIRED_ROUTE_REDIRECTS[requested];
    }
    if (OSV2_ROUTES.has(requested as OSV2Route)) {
      return normalizeRoute(requested as OSV2Route);
    }
  }
  // The member home owns entry to every course; explicit welcome links remain valid.
  return 'core';
}

/**
 * `?osRoute=after-checkout` — the guest checkout landing (Phase 3,
 * docs/plans/2026-08-25-sku-shelf-portal-plan.md).
 *
 * Deliberately NOT a member of `OSV2Route`: it is not a shell destination (no
 * rail, no breadcrumb, no top bar) and it is the only screen here that renders
 * for someone with no account. It is a standalone landing that sits above the
 * sign-in gate.
 *
 * Existing paid purchases can always reach this page, regardless of the
 * feature flag for creating new guest checkouts.
 */
const AFTER_CHECKOUT_PARAM_VALUE = 'after-checkout';

function isAfterCheckoutRequested(): boolean {
  return new URLSearchParams(window.location.search).get('osRoute') === AFTER_CHECKOUT_PARAM_VALUE;
}

/**
 * `?sku=` — which product the `product` route is showing.
 *
 * Kept as its own URL param rather than folded into `osRoute` so the route
 * union stays a fixed set of screens and the product id stays data. A missing
 * or unknown id sends the buyer back to their products (handled in the page).
 */
function readProductSku(): string | null {
  const value = new URLSearchParams(window.location.search).get('sku');
  return value && value.trim() ? value.trim() : null;
}

/**
 * Tiny loading shimmer shown while a code-split route chunk is fetched.
 * Brand-styled -- the page area pulses on the engineering grid backdrop
 * instead of a generic spinner.
 */
function RouteLoadingState({ route }: { route: OSV2Route }) {
  // `product` is a route id, not a word anybody bought. Now that the product
  // page is split, this fallback is the first thing a member sees on it.
  const label = route === 'product' ? 'your product' : route;

  return (
    <div className="grid h-[calc(100dvh-56px)] place-items-center">
      <div className="flex items-center gap-3">
        <span className="block h-2 w-2 animate-fd-pulse rounded-full bg-fd-accent" />
        <span className="font-geist-mono text-[11px] font-semibold uppercase tracking-[0.10em] text-fd-text-subtle">
          Loading {label}…
        </span>
      </div>
    </div>
  );
}

export default function OSV2App() {
  const [route, setRoute] = useState<OSV2Route>(getInitialRoute);
  const [productSku, setProductSku] = useState<string | null>(readProductSku);
  const [afterCheckout, setAfterCheckout] = useState<boolean>(isAfterCheckoutRequested);
  const auth = useAppAuth();

  useEffect(() => {
    const currentUrl = new URL(window.location.href);
    const requested = currentUrl.searchParams.get('osRoute');
    if (!requested) return;
    // Retired-route redirect first (e.g. business-xray -> loop-install).
    const retiredTarget = RETIRED_ROUTE_REDIRECTS[requested];
    if (retiredTarget) {
      currentUrl.searchParams.set('osRoute', retiredTarget);
      window.history.replaceState({}, '', currentUrl);
      return;
    }
    if (!OSV2_ROUTES.has(requested as OSV2Route)) return;
    const normalizedRoute = normalizeRoute(requested as OSV2Route);
    if (normalizedRoute === requested) return;
    currentUrl.searchParams.set('osRoute', normalizedRoute);
    window.history.replaceState({}, '', currentUrl);
  }, []);

  const handleNavigate = (nextRoute: OSV2Route, sku: string | null = null) => {
    const normalizedRoute = normalizeRoute(nextRoute);
    setAfterCheckout(false);
    setRoute(normalizedRoute);
    setProductSku(normalizedRoute === 'product' ? sku : null);
    const nextUrl = new URL(window.location.href);
    nextUrl.searchParams.set('osRoute', normalizedRoute);
    // `sku` only means something on the product route. Leaving a stale one in
    // the URL would make a shared link open the wrong screen.
    if (normalizedRoute === 'product' && sku) {
      nextUrl.searchParams.set('sku', sku);
    } else {
      nextUrl.searchParams.delete('sku');
    }
    // `lessonKey` names which lesson of a product is open. It belongs to one
    // course and is set by the delivery page itself, so navigating anywhere
    // clears it rather than leaving a stale one to open the wrong thing on a
    // shared link — lesson keys like `notes` or `setup` collide across products
    // readily, so a carried key opens a real lesson in the wrong course.
    nextUrl.searchParams.delete('lessonKey');
    // `module` did the same job before the classroom. Still cleared, so an old
    // bookmark does not leave a dead param sitting in every link from here on.
    nextUrl.searchParams.delete('module');
    window.history.replaceState({}, '', nextUrl);
  };

  // Guest checkout landing. Sits ABOVE the sign-in gate on purpose: the buyer
  // has paid but may not have an account yet, and the generic gate would tell
  // them to sign in without the one thing that matters — same email.
  if (afterCheckout) {
    return <AfterCheckout onContinue={() => handleNavigate('core')} />;
  }

  if (!auth.isLoaded) {
    return <OSAuthLoadingScreen />;
  }

  if (!auth.isSignedIn) {
    return <Suspense fallback={<OSAuthLoadingScreen />}><LearningPortal /></Suspense>;
  }

  return (
    <Suspense fallback={<RouteLoadingState route={route} />}>
      <LearningPortal osRoute={route} productSku={productSku} onNavigate={(next, sku) => handleNavigate(next as OSV2Route, sku)} legacyContent={<>
        {route === 'welcome' && <Welcome />}
        {route === 'loop-install' && <LoopInstallWorkspace />}
        {route === 'operator-dashboard' && <OperatorDashboard />}
        {route === 'command-centre' && <CommandCentre />}
        {route === 'weekly-review' && <WeeklyReview />}
        {route === 'build-room' && <BuildRoom />}
        {(route === 'lab' || route === 'ask-tom') && <LabDesk />}
        {route === 'community' && <Community />}
        {route === 'labs' && <Labs />}
        {route === 'acquisition-engine' && <EngineWorkspace engine="acquisition" />}
        {route === 'experience-engine' && <EngineWorkspace engine="experience" />}
        {route === 'case-study' && <CaseStudy />}
        {route === 'settings' && <Settings />}
      </>} />
    </Suspense>
  );
}

function OSAuthLoadingScreen() {
  return (
    <main className="grid min-h-[100dvh] place-items-center bg-fd-bg px-6 font-geist text-fd-text">
      <div className="flex items-center gap-3 rounded-[10px] border border-fd-border bg-fd-paper px-4 py-3 shadow-[0_18px_40px_-30px_rgba(20,16,8,0.24)]">
        <Loader2 className="size-4 animate-spin text-fd-accent-deep" aria-hidden="true" />
        <span className="font-geist-mono text-[11px] font-semibold uppercase tracking-[0.12em] text-fd-text-muted">
          Checking your Lab account
        </span>
      </div>
    </main>
  );
}
